/** CMS collections: types (schema editor, presets) and items (form generated from the schema). Route /sites/:id/collections-cms */
import React, { useMemo, useState } from 'react';
import {
    Button, Card, Checkbox, Collapse, Drawer, Dropdown, Empty, Input, Popconfirm, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import FieldForm, { FieldCtx } from './editor/FieldForm';
import { slugify, toastErr, useLoad, useSite } from './common';
import { uploadMedia } from './Media';

const { Text } = Typography;
const TYPES = ['text', 'textarea', 'richtext', 'image', 'link', 'number', 'boolean', 'select', 'date', 'list'];

/** The collection schema → the editor's field model. date and list have no editor field of their own. */
const toFields = (schema) => {
    const out = {};
    (schema || []).forEach((f) => {
        const label = f.required ? `${f.label} *` : f.label;
        if (f.type === 'select') out[f.name] = { type: 'select', label, options: [{ value: '', label: '-' }, ...(f.options || []).map((o) => ({ value: o, label: o }))] };
        else if (f.type === 'list') out[f.name] = { type: 'textarea', label };
        else if (f.type === 'date') out[f.name] = { type: 'text', label, help: 'YYYY-MM-DD' };
        else out[f.name] = { type: f.type, label };
    });
    return out;
};
const toForm = (schema, data) => {
    const v = { ...(data || {}) };
    (schema || []).forEach((f) => {
        if (f.type === 'list') v[f.name] = (v[f.name] || []).join('\n');
        if (v[f.name] === null || v[f.name] === undefined) {
            v[f.name] = f.type === 'image' ? { src: '', alt: '' } : f.type === 'link' ? { href: '', label: '' } : f.type === 'boolean' ? false : f.type === 'number' ? 0 : '';
        }
    });
    return v;
};
const fromForm = (schema, v) => {
    const d = { ...v };
    (schema || []).forEach((f) => { if (f.type === 'list') d[f.name] = String(d[f.name] || '').split('\n').map((x) => x.trim()).filter(Boolean); });
    return d;
};
const titleOf = (schema, d) => d?.title || d?.name || (schema || []).map((f) => d?.[f.name]).find((x) => typeof x === 'string' && x) || '-';

const SchemaDrawer = ({ type, onClose, onSaved }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [v, setV] = useState(type);
    const [saving, setSaving] = useState(false);
    const isNew = !type.id;
    const setF = (i, patch) => setV((x) => ({ ...x, fields: x.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)) }));
    const save = async () => {
        setSaving(true);
        try {
            const body = { name: v.name, url_prefix: v.url_prefix, fields: v.fields, slug: v.slug };
            if (isNew) await api.post('/cms/types', body); else await api.put(`/cms/types/${type.id}`, body);
            message.success(t('Saved — publish to apply'));
            onSaved();
        } catch (e) { toastErr(e); } finally { setSaving(false); }
    };
    return (
        <Drawer open onClose={onClose} width={Math.min(720, window.innerWidth)} title={isNew ? t('New collection') : v.name}
            extra={<Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>{t('Save')}</Button>}>
            <Space direction="vertical" style={{ width: '100%' }}>
                <Input addonBefore={t('Name')} value={v.name} onChange={(e) => setV({ ...v, name: e.target.value, ...(isNew ? { slug: slugify(e.target.value) } : {}) })} />
                <Input addonBefore={t('URL prefix')} placeholder="team" value={v.url_prefix} onChange={(e) => setV({ ...v, url_prefix: e.target.value })} />
                <Table size="small" pagination={false} rowKey={(_, i) => i} dataSource={v.fields} scroll={{ x: 620 }} columns={[
                    { title: t('Label'), dataIndex: 'label', render: (x, _, i) => <Input size="small" value={x} onChange={(e) => setF(i, { label: e.target.value })} /> },
                    { title: t('Field'), dataIndex: 'name', width: 130, render: (x, _, i) => <Input size="small" value={x} onChange={(e) => setF(i, { name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} /> },
                    { title: t('Type'), dataIndex: 'type', width: 110, render: (x, _, i) => <Select size="small" style={{ width: '100%' }} value={x} onChange={(type2) => setF(i, { type: type2 })} options={TYPES.map((o) => ({ value: o, label: o }))} /> },
                    { title: t('Options'), dataIndex: 'options', width: 150, render: (x, r, i) => (r.type === 'select' ? <Input size="small" value={(x || []).join(', ')} onChange={(e) => setF(i, { options: e.target.value.split(',').map((o) => o.trimStart()) })} /> : null) },
                    { title: t('Required'), dataIndex: 'required', width: 70, align: 'center', render: (x, _, i) => <Checkbox checked={!!x} onChange={(e) => setF(i, { required: e.target.checked })} /> },
                    { title: '', key: 'd', width: 36, render: (_, __, i) => <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => setV({ ...v, fields: v.fields.filter((_x, j) => j !== i) })} /> },
                ]} />
                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => setV({ ...v, fields: [...v.fields, { name: `field_${v.fields.length + 1}`, label: '', type: 'text', required: false }] })}>{t('Add field')}</Button>
            </Space>
        </Drawer>
    );
};

const ItemDrawer = ({ type, item, onClose, onSaved }) => {
    const { t } = useTranslation();
    const { api, siteId } = useSite();
    const [data, setData] = useState(() => toForm(type.fields, item?.data));
    const [slug, setSlug] = useState(item?.slug || '');
    const [saving, setSaving] = useState(false);
    const fields = useMemo(() => toFields(type.fields), [type]);
    const uploadImage = async (file) => {
        const m = (await uploadMedia(siteId, [file]))[0];
        return m ? { src: m.url, width: m.width || 0, height: m.height || 0 } : null;
    };
    const save = async (status) => {
        setSaving(true);
        try {
            const body = { data: fromForm(type.fields, data), status, slug };
            if (item?.id) await api.put(`/cms/types/${type.id}/items/${item.id}`, body); else await api.post(`/cms/types/${type.id}/items`, body);
            message.success(t('Saved'));
            onSaved();
        } catch (e) { toastErr(e); } finally { setSaving(false); }
    };
    return (
        <Drawer open onClose={onClose} width={Math.min(640, window.innerWidth)} title={titleOf(type.fields, data)}
            extra={<Space>
                <Button loading={saving} onClick={() => save('draft')}>{t('Save draft')}</Button>
                <Button type="primary" loading={saving} onClick={() => save('published')}>{t('Publish')}</Button>
            </Space>}>
            <FieldCtx.Provider value={{ siteId, uploadImage, pages: [], slotCounts: {} }}>
                <FieldForm fields={fields} value={data} onChange={setData} />
            </FieldCtx.Provider>
            {type.url_prefix && <Input addonBefore={`/${type.url_prefix}/`} placeholder={t('Automatic')} value={slug} onChange={(e) => setSlug(e.target.value)} style={{ marginTop: 12 }} />}
        </Drawer>
    );
};

const Items = ({ type }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const { data, reload, setData } = useLoad(() => api.get(`/cms/types/${type.id}/items`), [type.id]);
    const [edit, setEdit] = useState(null);
    const items = data?.items || [];
    const move = async (i, d) => {
        const next = [...items];
        const [x] = next.splice(i, 1);
        next.splice(i + d, 0, x);
        setData({ ...data, items: next });
        try { await api.post(`/cms/types/${type.id}/items/reorder`, { ids: next.map((n) => n.id) }); } catch (e) { toastErr(e); reload(); }
    };
    return (
        <>
            <Button size="small" type="primary" icon={<PlusOutlined />} style={{ marginBottom: 8 }} onClick={() => setEdit({})}>{t('Add item')}</Button>
            <Table size="small" rowKey="id" pagination={false} dataSource={items} scroll={{ x: 480 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No items yet')} /> }} columns={[
                    { title: t('Title'), key: 't', render: (_, r) => <a onClick={() => setEdit(r)}>{titleOf(type.fields, r.data)}</a> },
                    { title: t('Status'), dataIndex: 'status', width: 100, render: (s) => <Tag color={s === 'published' ? 'green' : 'default'}>{t(s === 'published' ? 'Published' : 'Draft')}</Tag> },
                    { title: '', key: 'a', width: 130, render: (_, r, i) => (
                        <Space size={0}>
                            <Button size="small" type="text" disabled={i === 0} icon={<ArrowUpOutlined />} onClick={() => move(i, -1)} />
                            <Button size="small" type="text" disabled={i === items.length - 1} icon={<ArrowDownOutlined />} onClick={() => move(i, 1)} />
                            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEdit(r)} />
                            <Popconfirm title={t('Delete?')} onConfirm={() => api.del(`/cms/types/${type.id}/items/${r.id}`).then(reload).catch(toastErr)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>
                        </Space>
                    ) },
                ]} />
            {edit && <ItemDrawer type={type} item={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
        </>
    );
};

const Content = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const { data: types, reload } = useLoad(() => api.get('/cms/types'), [api]);
    const { data: presets } = useLoad(() => api.get('/cms/presets'), [api]);
    const [schema, setSchema] = useState(null);
    const list = Array.isArray(types) ? types : [];
    const taken = new Set(list.map((x) => x.slug));
    const addPreset = async (preset) => {
        try { await api.post('/cms/types', { preset }); message.success(t('Saved — publish to apply')); reload(); } catch (e) { toastErr(e); }
    };
    return (
        <Card size="small" title={t('Collections')} extra={(
            <Space>
                <Dropdown menu={{ items: (presets || []).filter((p) => !taken.has(p.key)).map((p) => ({ key: p.key, label: t(p.name), onClick: () => addPreset(p.key) })) }}>
                    <Button type="primary" icon={<PlusOutlined />}>{t('Add collection')}</Button>
                </Dropdown>
                <Button onClick={() => setSchema({ name: '', slug: '', url_prefix: '', fields: [{ name: 'title', label: 'Title', type: 'text', required: true }] })}>{t('Custom')}</Button>
            </Space>
        )}>
            {!list.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No collections yet')} /> : (
                <Collapse items={list.map((c) => ({
                    key: c.id,
                    label: <Space><Text strong>{c.name}</Text><Text type="secondary">{c.url_prefix ? `/${c.url_prefix}/…` : ''}</Text></Space>,
                    extra: (
                        <span onClick={(e) => e.stopPropagation()}>
                            <Button size="small" type="text" icon={<SettingOutlined />} onClick={() => setSchema(c)} />
                            <Popconfirm title={t('Delete this collection and its items?')} onConfirm={() => api.del(`/cms/types/${c.id}`).then(reload).catch(toastErr)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>
                        </span>
                    ),
                    children: <Items type={c} />,
                }))} />
            )}
            {schema && <SchemaDrawer type={schema} onClose={() => setSchema(null)} onSaved={() => { setSchema(null); reload(); }} />}
        </Card>
    );
};

export default Content;
